import { getCLS, getFID, getFCP, getLCP, getTTFB } from 'web-vitals';
import React from 'react';
import config from '../config/environment';

import { createLogger } from './logger';

const logger = createLogger('performance');

// Performance monitoring utilities
export const PerformanceMetrics = {
  // Core Web Vitals
  CLS: 'Cumulative Layout Shift',
  FID: 'First Input Delay',
  LCP: 'Largest Contentful Paint',

  // Other metrics
  FCP: 'First Contentful Paint',
  TTFB: 'Time to First Byte',
};

const logMetric = metric => {
  if (config.features.debugMode) {
    logger.debug(`Performance Metric - ${metric.name}:`, {
      value: metric.value,
      rating: metric.rating,
      delta: metric.delta,
      id: metric.id,
    });
  }

  // TODO: Send to analytics service
  // Example: analytics.track('performance_metric', { ...metric });

  // TODO: Send to performance monitoring service
  // Example: performanceMonitoring.report(metric);
};

export const initPerformanceMonitoring = () => {
  if (!config.features.enableAnalytics) {
    return;
  }

  try {
    // Core Web Vitals
    getCLS(logMetric);
    getFID(logMetric);
    getLCP(logMetric);

    // Additional metrics
    getFCP(logMetric);
    getTTFB(logMetric);

    logger.debug('Performance monitoring initialized');
  } catch (error) {
    logger.warn('Failed to initialize performance monitoring:', error);
  }
};

export const measureCustomMetric = (name, startTime) => {
  const endTime = performance.now();
  const duration = endTime - startTime;

  const metric = {
    name: `custom_${name}`,
    value: duration,
    timestamp: Date.now(),
  };

  logMetric(metric);
  return duration;
};

export const markStart = name => {
  const startTime = performance.now();
  performance.mark(`${name}_start`);
  return startTime;
};

export const markEnd = (name, startTime) => {
  performance.mark(`${name}_end`);
  performance.measure(name, `${name}_start`, `${name}_end`);

  if (startTime) {
    return measureCustomMetric(name, startTime);
  }

  const measure = performance.getEntriesByName(name, 'measure')[0];
  if (measure) {
    logMetric({
      name,
      value: measure.duration,
      timestamp: Date.now(),
    });
    return measure.duration;
  }

  return null;
};

// React component performance wrapper
export const withPerformanceTracking = (WrappedComponent, componentName) => {
  return function PerformanceTrackedComponent(props) {
    const startTime = React.useMemo(
      () => markStart(`component_${componentName}_render`),
      []
    );

    // Track component mount time
    React.useEffect(() => {
      markEnd(`component_${componentName}_render`, startTime);
    }, [startTime]);

    return React.createElement(WrappedComponent, props);
  };
};

// Hook for tracking async operations
export const usePerformanceTracker = () => {
  const trackAsyncOperation = React.useCallback(async (name, asyncFn) => {
    const startTime = markStart(`async_${name}`);
    try {
      const result = await asyncFn();
      markEnd(`async_${name}`, startTime);
      return result;
    } catch (error) {
      markEnd(`async_${name}`, startTime);
      throw error;
    }
  }, []);

  return { trackAsyncOperation };
};
